import { useEffect, useRef } from "react";

/**
 * Give an open sheet or modal its own history entry, so the phone's Back
 * closes it instead of leaving the screen — on a phone, Back over an overlay
 * means "dismiss this", and losing the whole screen to it reads as a crash.
 *
 * Raw history rather than the router, deliberately: the sign-in sheet mounts
 * outside the Router, and the pushed twin spreads the current entry's state so
 * the router's own bookkeeping (its index) survives the round trip. Closing by
 * any other means (tap outside, Done) walks the twin back off the stack, so
 * entries never pile up.
 *
 * Pass `true` for overlays that mount only while open; pass the open flag for
 * ones that stay mounted.
 */
export const useSheetHistory = (open, onClose) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pushedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ ...window.history.state, fuellySheet: true }, "");
    pushedRef.current = true;
    const onPop = () => {
      // Back while the overlay is open: the browser already removed the twin.
      pushedRef.current = false;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (pushedRef.current) {
        pushedRef.current = false;
        window.history.back();
      }
    };
  }, [open]);
};
